import Vue from "vue";
import Loading from "./Loading.vue";

// 创建 Loading 构造器
const LoadingConstructor = Vue.extend(Loading);

// 默认配置
const defaults = {
  type: "dots",
  background: "rgba(255, 255, 255, 0.7)",
  spinnerColor: "",
  text: "",
  textColor: "",
  zIndex: 2000,
  customClass: "",
  target: ""
};

// 辅助方法：获取配置选项
function getOptions(value, modifiers, arg) {
  const options = {
    visible: false
  };

  // 处理参数
  if (typeof value === "object") {
    Object.assign(options, value);
  } else {
    options.visible = !!value;
  }

  // 处理修饰符
  if (modifiers.fullscreen) {
    options.target = "body";
  }

  // 处理参数 - 类型设置
  if (arg) {
    options.type = arg;
  }

  // 合并默认配置
  return { ...defaults, ...options };
}

const loadingDirective = {};

loadingDirective.install = Vue => {
  // 注册全局指令 v-newloading
  Vue.directive("newloading", {
    bind(el, binding) {
      const { value, modifiers, arg } = binding;

      // 获取配置
      const options = getOptions(value, modifiers, arg);

      // 创建 loading 实例
      const loadingInstance = new LoadingConstructor();

      // 初始化实例配置
      Object.assign(loadingInstance, options);

      // 保存实例到元素上
      el._loadingInstance = loadingInstance;
      el._originalPosition = "";
    },

    inserted(el, binding) {
      const { value, modifiers, arg } = binding;
      const instance = el._loadingInstance;

      // 更新配置
      const options = getOptions(value, modifiers, arg);
      Object.assign(instance, options);

      // 全屏模式
      if (modifiers.fullscreen) {
        document.body.appendChild(instance.$mount().$el);
        document.body.style.overflow = "hidden";
      }
      // 指定目标元素
      else if (options.target) {
        const targetEl = document.querySelector(options.target);
        if (targetEl) {
          targetEl.appendChild(instance.$mount().$el);
          targetEl.style.position = "relative";
        } else {
          console.warn(`v-newloading: target element "${options.target}" not found.`);
          el.appendChild(instance.$mount().$el);
        }
      }
      // 默认插入到当前元素
      else {
        // 保存原始位置
        if (el !== document.body) {
          el._originalPosition = getComputedStyle(el).position;
          if (el._originalPosition === "static") {
            el.style.position = "relative";
          }
        }
        el.appendChild(instance.$mount().$el);
      }

      // 显示或隐藏
      if (options.visible) {
        instance.show(options);
      } else {
        instance.hide();
      }
    },

    update(el, binding) {
      const { value, oldValue, modifiers, arg } = binding;
      const instance = el._loadingInstance;

      if (value === oldValue && !arg) return;

      // 更新配置
      const options = getOptions(value, modifiers, arg);

      // 更新实例
      if (instance) {
        if (options.visible) {
          instance.show(options);
        } else {
          instance.hide();
        }
      }
    },

    unbind(el) {
      const instance = el._loadingInstance;

      if (instance) {
        instance.hide();
        setTimeout(() => {
          if (instance.$el && instance.$el.parentNode) {
            instance.$el.parentNode.removeChild(instance.$el);
          }
          instance.$destroy();
        }, 300);
      }

      // 恢复原始位置
      if (el._originalPosition && el.style) {
        el.style.position = el._originalPosition;
      }
    }
  });

  // 注册全局服务方法
  Vue.prototype.$newloading = {
    // 服务方式调用
    service(options = {}) {
      const mergedOptions = { ...defaults, ...options };

      const instance = new LoadingConstructor();
      const vm = instance.$mount();

      Object.assign(instance, mergedOptions);

      if (mergedOptions.target) {
        const targetEl = document.querySelector(mergedOptions.target);
        if (targetEl) {
          targetEl.appendChild(vm.$el);
          targetEl.style.position = "relative";
        } else {
          document.body.appendChild(vm.$el);
        }
      } else {
        document.body.appendChild(vm.$el);
        document.body.style.overflow = "hidden";
      }

      instance.show(mergedOptions);

      return {
        close() {
          instance.hide();
          setTimeout(() => {
            if (vm.$el && vm.$el.parentNode) {
              vm.$el.parentNode.removeChild(vm.$el);
            }
            instance.$destroy();
            document.body.style.overflow = "";
          }, 300);
        }
      };
    },

    // 全局配置
    setDefaults(newDefaults) {
      Object.assign(defaults, newDefaults);
    }
  };
};

export default loadingDirective;
